import type { Lead, LeadOutreachSafetySummary } from '@/types/leads';

export type OutreachSafetyState = 'ready' | 'needs_review' | 'account_expansion_review' | 'blocked';

export interface OutreachSafetyInput {
  status?: Lead['status'] | null;
  do_not_contact?: boolean | null;
  suppression_reason?: Lead['suppression_reason'] | null;
  match_status?: string | null;
  matched_entity_type?: Lead['matched_entity_type'] | null;
  matched_entity_id?: string | null;
  outreach_suppression?: LeadOutreachSafetySummary | null;
}

export interface OutreachSafetyResult {
  state: OutreachSafetyState;
  label: string;
  detail: string;
  blocksOutreach: boolean;
}

const clearMatchStatuses = new Set(['clear', 'cleared', 'checked', 'no_match', 'no_conflict', 'unmatched']);

export const suppressionReasonLabels: Record<NonNullable<Lead['suppression_reason']>, string> = {
  do_not_contact: 'Do not contact',
  unsubscribe: 'Unsubscribed',
  hard_bounce: 'Hard bounce',
  spam_complaint: 'Spam complaint',
  invalid_email: 'Invalid email',
  current_customer: 'Current customer',
  existing_customer: 'Existing customer',
  open_deal: 'Open deal',
  inbound_dup: 'Inbound duplicate',
  manual_block: 'Manual block',
  other: 'Suppressed',
};

const matchedEntityLabels: Record<NonNullable<Lead['matched_entity_type']>, string> = {
  customer_location: 'Customer location',
  open_deal: 'Open deal',
  blueprint_lead: 'Blueprint lead',
  existing_lead: 'Existing lead',
};

const blockedMatchedEntityTypes = new Set<NonNullable<Lead['matched_entity_type']>>(['customer_location', 'open_deal']);

const blockedMatchStatuses = new Set(['blocked', 'suppressed', 'do_not_contact']);

function titleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function getMatchedEntitySafety(
  matchedEntityType: NonNullable<Lead['matched_entity_type']>,
  matchedEntityId?: string | null
): OutreachSafetyResult {
  const entityLabel = matchedEntityLabels[matchedEntityType];
  if (blockedMatchedEntityTypes.has(matchedEntityType)) {
    return {
      state: 'blocked',
      label: entityLabel,
      detail: `Matched ${entityLabel.toLowerCase()}${matchedEntityId ? ` ${matchedEntityId}` : ''}; do not include in cold outreach.`,
      blocksOutreach: true,
    };
  }

  return {
    state: 'needs_review',
    label: entityLabel,
    detail: `Matched ${entityLabel.toLowerCase()}${matchedEntityId ? ` ${matchedEntityId}` : ''}; review before outreach.`,
    blocksOutreach: true,
  };
}

export function getOutreachSafety(lead: OutreachSafetyInput): OutreachSafetyResult {
  if ((lead.outreach_suppression?.active_suppression_count ?? 0) > 0) {
    const reason = lead.outreach_suppression?.primary_suppression_reason;
    const label = reason ? suppressionReasonLabels[reason] : 'Suppressed';
    const sources = lead.outreach_suppression?.active_suppression_sources;
    return {
      state: 'blocked',
      label,
      detail: `Matched an active do-not-touch record${sources ? ` from ${sources}` : ''}.`,
      blocksOutreach: true,
    };
  }

  if (lead.do_not_contact) {
    return {
      state: 'blocked',
      label: 'Do not contact',
      detail: 'This lead is marked do-not-contact and must not be included in outreach.',
      blocksOutreach: true,
    };
  }

  if (lead.status === 'suppressed' || lead.status === 'do_not_contact' || lead.status === 'disqualified') {
    return {
      state: 'blocked',
      label: titleCase(lead.status),
      detail: 'This lead status blocks outreach.',
      blocksOutreach: true,
    };
  }

  if (lead.status === 'converted') {
    return {
      state: 'blocked',
      label: 'Converted',
      detail: 'This lead has already been converted and must not be included in cold outreach.',
      blocksOutreach: true,
    };
  }

  if (lead.suppression_reason) {
    return {
      state: 'blocked',
      label: suppressionReasonLabels[lead.suppression_reason],
      detail: 'This lead matched a suppression rule and must not be included in outreach.',
      blocksOutreach: true,
    };
  }

  const normalizedMatchStatus = lead.match_status?.trim().toLowerCase();
  if (!normalizedMatchStatus) {
    if (lead.matched_entity_type) {
      return getMatchedEntitySafety(lead.matched_entity_type, lead.matched_entity_id);
    }

    return {
      state: 'needs_review',
      label: 'Cross-check pending',
      detail: 'No admin customer/contact/deal check result is recorded yet.',
      blocksOutreach: true,
    };
  }

  if (normalizedMatchStatus === 'account_expansion_review') {
    return {
      state: 'account_expansion_review',
      label: 'Account expansion review',
      detail: 'This appears related to an existing TPB customer, deal, operator, or brand. Treat as account expansion until a human releases it.',
      blocksOutreach: true,
    };
  }

  if (blockedMatchStatuses.has(normalizedMatchStatus)) {
    return {
      state: 'blocked',
      label: titleCase(normalizedMatchStatus),
      detail: 'The latest safety cross-check recorded a blocking result.',
      blocksOutreach: true,
    };
  }

  if (lead.matched_entity_type) {
    return getMatchedEntitySafety(lead.matched_entity_type, lead.matched_entity_id);
  }

  if (!clearMatchStatuses.has(normalizedMatchStatus)) {
    return {
      state: 'needs_review',
      label: titleCase(normalizedMatchStatus),
      detail: 'The cross-check result needs review before outreach.',
      blocksOutreach: true,
    };
  }

  return {
    state: 'ready',
    label: 'Safety clear',
    detail: 'No recorded customer, deal, duplicate, or do-not-contact conflict. Human approval is still required before outreach.',
    blocksOutreach: false,
  };
}

/** Hard blocks only — pending cross-check / needs_review do not exclude week-queue ranking. */
export function blocksWeekQueueEligibility(lead: OutreachSafetyInput): boolean {
  return getOutreachSafety(lead).state === 'blocked';
}
